import { CheckCircle, TrendingUp, Wrench } from 'lucide-react';

import type { FeatureFlagChecker } from '@/hooks/use-feature-flag';

import { newsStyles } from '../../styles';
import type { News } from '../../types';
import { WhatsNewLink } from '../../whats-new-link';

import { PageReference } from './page-reference';

export const getFilteredCustomersNews = (
  featureFlag: FeatureFlagChecker,
  customerId: string,
): News[] => {
  const styles = newsStyles();

  const news = [];

  if (featureFlag('assets') && ['31', '29', '48', '57'].includes(customerId)) {
    news.push({
      key: 'available-energy-calculation-update',
      icon: <TrendingUp className={styles.icon()} />,
      expiredAt: new Date('2025-03-19'),
      priority: 2,
      label: (
        <>
          <WhatsNewLink to={'/benchmark'} className="mb-2">
            Improved Available Energy
          </WhatsNewLink>
          <div>
            The overview table on the{' '}
            <PageReference text="Benchmark" href="/benchmark" /> page now shows
            updated values for available energy, that better reflect the present
            state of each site.
          </div>
          <div>
            Reason: We changed from our old assumption "units that produce no
            results, have an available energy of 0" to a more probable "units
            that did not produce results likely have the average energy of the
            rest of the site available".
          </div>
        </>
      ),
    });
  }

  if (customerId === '62') {
    news.push(
      {
        key: 'maintenance-reminder-2025-12-05',
        priority: 5, // High priority for maintenance reminder news
        icon: <Wrench className="size-full text-amber-700" />,
        expiredAt: new Date('2025-12-10'),
        isCollapsible: false,
        label: (
          <>
            <span className="font-semibold text-amber-700">
              Scheduled Maintenance Notice
            </span>
            <p>
              We're preparing to add rack level to our analytics for your site.
              Expect a short downtime and some usability issues during the next
              24 hours. Don't worry — it'll be over in a flash, and we'll be
              back better than ever! Thanks for your patience!
            </p>
          </>
        ),
      },
      // Migration is complete! Give the customer a warm nudge to try the new rack level
      {
        key: 'migration-successful-2025-12-10',
        priority: 5,
        icon: <CheckCircle className="size-full text-green-700" />,
        expiredAt: new Date('2025-12-20'),
        isCollapsible: false,
        label: (
          <>
            <span className="font-semibold text-green-700">
              Rack Level Unlocked – Explore More!
            </span>
            <p>
              Good news: your <strong>rack level</strong> is now live in the
              platform! This new level lets you dive deeper into your site's
              performance. Check it out today and get the most from your
              analytics.
            </p>
            <p>Give it a try and let us know how it helps your workflow!</p>
          </>
        ),
      },
    );
  }

  return news;
};
